import React, { useCallback, useRef, useState } from 'react';

import { SELL_MY_CAR_LANDING_PAGE_V2 } from 'Utilities/wordpress';

import { ComponentContent, CONTENT_SIZES } from 'Layout';

import StickyVRM from 'Components/misc/StickyVRM/StickyVRM';
import { TrustpilotReviewsCarousel } from 'Components/reviews/ReviewsCarousel/TrustpilotReviewsCarousel';
import DialogLoader from 'Components/transitions/DialogLoader/DialogLoader';

import BodyArea from '../../common/BodyArea/BodyArea';
import FAQSection, { createFAQData } from '../../common/FAQSection/FAQSection';
import { RoundedSection } from '../../common/RoundedSection/RoundedSection';
import Status404 from '../../error/404';
import DealerLogos from '../../fastlane/dealerLogos/DealerLogos';
import Hero from '../../fastlane/home/Hero/Hero';
import USP from '../../fastlane/home/Usp/Usp';
import RecentSales from '../../fastlane/recentSales/RecentSales';
import { VideoSection } from '../../fastlane/videoSection/VideoSection';
import { LandingPagesProps } from '../@types/landingPages';
import HowItWorks from '../Components/HowItWorks/HowItWorks';
import { RelatedGuidesSection } from '../Components/RelatedGuidesSection/RelatedGuidesSection';
import { useLandingPageData } from '../Hooks/useLandingPageData';

import { landingPageSectionLoadEvent, PAGE_SECTIONS } from './LandingPage.helpers';

import styles from './LandingPageClassic.scss';

const LandingPageClassic: React.FC<LandingPagesProps> = ({ match }) => {
  const {
    acf,
    bodyAreaSections,
    getRelatedGuidesContent,
    loading,
    stickyVRM,
  } = useLandingPageData();

  const [failedReviewsCarousel, setFailedReviewsCarousel] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { faq_title: faqTitle, related_guide_header: relatedGuideTitle } = acf || {};
  const faq = createFAQData(acf);
  const isFAQEmpty = (!Array.isArray(faq) || !faq.length);

  const relatedGuides = getRelatedGuidesContent(acf);
  const isRelatedGuidesEmpty = (!Array.isArray(relatedGuides) || !relatedGuides.length);

  const handleFailedReviewsCarousel = useCallback(() => {
    setFailedReviewsCarousel((prev) => !prev);
  }, []);

  if (loading) {
    return <DialogLoader />;
  }
  if (!acf) {
    return <Status404 />;
  }

  const $content = contentRef.current;

  // V2 layout
  if (match.url === SELL_MY_CAR_LANDING_PAGE_V2) {
    return (
      <div ref={contentRef} className={styles.viewV2}>
        <div className={styles.heroSection}>
          {acf?.header_title && (
            <Hero
              onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.HERO)}
              subTitle={acf.header_description?.trim()}
              title={acf.header_title}
            />
          )}
          <USP onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.USP)} />
          <HowItWorks className={styles.stepsSectionWrapper} stepsClassName={styles.steps} />
          <ComponentContent
            className={failedReviewsCarousel ? styles.trustpilotEmpty : styles.trustpilot}
            maxWidth={CONTENT_SIZES.none}
          >
            <TrustpilotReviewsCarousel
              largeTitle
              failedLoad={failedReviewsCarousel}
              onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.TRUSTPILOT)}
              setFailedLoad={handleFailedReviewsCarousel}
            />
          </ComponentContent>
        </div>
      </div>
    );
  }

  return (
    <div ref={contentRef} className={styles.view}>
      <div className={styles.heroSection}>
        {
          stickyVRM && $content && (
            <StickyVRM
              {...{
                $content,
                mobileOnly: true,
              }}
            />
          )
        }

        {acf?.header_title && (
          <Hero
            onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.HERO)}
            subTitle={acf.header_description?.trim()}
            title={acf.header_title}
          />
        )}

        <RoundedSection withBottomLedge>
          <USP className={styles.usp} onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.USP)} />
        </RoundedSection>
      </div>

      <RoundedSection withBottomLedge withTopLedge className={styles.videoWrapper} theme="off-white">
        <VideoSection onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.VIDEO)} />
      </RoundedSection>

      <RecentSales onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.RECENT_SALES)}/>

      <RoundedSection withBottomLedge withTopLedge theme="off-white">
        <HowItWorks />
      </RoundedSection>

      <RoundedSection withBottomLedge withTopLedge>
        <ComponentContent
          className={failedReviewsCarousel ? styles.trustpilotEmpty : styles.trustpilot}
          maxWidth={CONTENT_SIZES.none}
        >
          <TrustpilotReviewsCarousel
            largeTitle
            failedLoad={failedReviewsCarousel}
            onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.TRUSTPILOT)}
            setFailedLoad={handleFailedReviewsCarousel}
          />
        </ComponentContent>
      </RoundedSection>

      <RoundedSection withBottomLedge withTopLedge theme="off-white">
        <DealerLogos onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.DEALER_LOGOS)} />
      </RoundedSection>

      <RoundedSection withBottomLedge withTopLedge className={styles.wrapper}>
        {bodyAreaSections.map(
          ({ contentBody, imgAlt, imgSrc, position, theme, title }) => (
            <BodyArea
              key={title || `section-${position}`}
              {...{ contentBody, imgAlt, imgSrc, position, theme, title }}
            />
          ),
        )}

        {!isFAQEmpty && (
          <FAQSection
            {...{ faq }}
            onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.FAQ)}
            title={faqTitle}
          />
        )}

        {!isRelatedGuidesEmpty && (
          <RelatedGuidesSection
            {...{ isFAQEmpty }}
            guides={relatedGuides}
            headerTitle={relatedGuideTitle ?? ''}
            onLoad={() => landingPageSectionLoadEvent(PAGE_SECTIONS.RELATED_GUIDES)}
            redirectToUrl={match.url}
          />
        )}
      </RoundedSection>
    </div>
  );
};

export default LandingPageClassic;
